import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Alert,
  Divider,
  Button,
} from "@mui/material";
import { History } from "@mui/icons-material";
import { useTheme } from "../../context/ThemeContext";

const VersionHistoryPanel = ({
  versionHistory,
  loading,
  selectedVersion,
  onVersionSelect,
  onClose,
  isViewingVersion,
}) => {
  const { isDarkMode } = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Version History
        </Typography>
        <Button variant="outlined" size="small" onClick={onClose}>
          Close
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading version history...
            </Typography>
          </Box>
        ) : versionHistory && versionHistory.length > 0 ? (
          <List sx={{ p: 0 }}>
            {versionHistory.map((version, index) => (
              <React.Fragment key={version.id}>
                <ListItem
                  disablePadding
                  sx={{
                    backgroundColor:
                      selectedVersion?.id === version.id
                        ? isDarkMode
                          ? "rgba(227, 98, 80, 0.15)"
                          : "rgba(227, 98, 80, 0.08)"
                        : "transparent",
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.02)",
                    },
                  }}
                >
                  <ListItemButton
                    onClick={() => onVersionSelect(version)}
                    sx={{
                      py: 2.5,
                      px: 2,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    {/* Timeline indicator */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mt: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor:
                            selectedVersion?.id === version.id
                              ? "#e36250"
                              : isDarkMode
                              ? "#555"
                              : "#ddd",
                          border: 2,
                          borderColor:
                            selectedVersion?.id === version.id
                              ? "#e36250"
                              : "divider",
                        }}
                      />
                      {index < versionHistory.length - 1 && (
                        <Box
                          sx={{
                            width: 2,
                            flex: 1,
                            minHeight: 50,
                            backgroundColor: isDarkMode ? "#333" : "#e0e0e0",
                          }}
                        />
                      )}
                    </Box>

                    {/* Version info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color:
                              selectedVersion?.id === version.id
                                ? "#e36250"
                                : isDarkMode
                                ? "#fff"
                                : "#1a1a1a",
                          }}
                        >
                          {formatDate(version.created_at)}
                        </Typography>
                        {selectedVersion?.id === version.id && (
                            <Box
                              sx={{
                                px: 1,
                                py: 0.25,
                                backgroundColor: "#e36250",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                }}
                              >
                                Selected
                              </Typography>
                            </Box>
                          )}
                      </Box>

                      {version.updated_by && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDarkMode ? "#9ca3af" : "#666",
                            mb: 0.5,
                          }}
                        >
                          By {version.updated_by}
                        </Typography>
                      )}

                      {version.agent_name && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDarkMode ? "#9ca3af" : "#888",
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Agent: {version.agent_name}
                        </Typography>
                      )}

                      {version.description && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDarkMode ? "#9ca3af" : "#888",
                            display: "block",
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {version.description}
                        </Typography>
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>
                {index < versionHistory.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
              p: 3,
              textAlign: "center",
            }}
          >
            <History sx={{ fontSize: 64, color: "#e36250", mb: 2, opacity: 0.3 }} />
            <Alert severity="info" sx={{ maxWidth: 400 }}>
              No version history available for this agent yet. Version history
              will be created when you save changes to your agent.
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VersionHistoryPanel;
